import { useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import type { Species } from '../api/species';

function speciesLabel(s: Species): string {
  const scientific = [s.genus, s.species].filter(Boolean).join(' ');
  return scientific ? `${s.commonName} (${scientific})` : s.commonName;
}

function speciesSearchText(s: Species): string {
  return [s.commonName, s.genus, s.species, s.subspecies, s.typeCategory, s.family, s.fullTaxonomicName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesQuery(s: Species, query: string): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = speciesSearchText(s);
  return tokens.every((token) => haystack.includes(token));
}

function sortSpecies(list: Species[]): Species[] {
  return [...list].sort((a, b) =>
    a.commonName.localeCompare(b.commonName, undefined, { sensitivity: 'base' }),
  );
}

interface SpeciesComboboxProps {
  id?: string;
  species: Species[];
  value: string;
  onChange: (speciesId: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  wrapperClassName?: string;
  optionsMaxHeightClass?: string;
}

export default function SpeciesCombobox({
  id = 'speciesId',
  species,
  value,
  onChange,
  required = false,
  disabled = false,
  placeholder = 'Search species…',
  wrapperClassName = 'mt-1',
  optionsMaxHeightClass = 'max-h-60',
}: SpeciesComboboxProps) {
  const [query, setQuery] = useState('');

  const sortedSpecies = useMemo(() => sortSpecies(species), [species]);

  const selected = useMemo(
    () => sortedSpecies.find((s) => s.id === value) ?? null,
    [sortedSpecies, value],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return sortedSpecies;
    return sortedSpecies.filter((s) => matchesQuery(s, query));
  }, [sortedSpecies, query]);

  return (
    <Combobox
      as="div"
      value={selected}
      onChange={(next: Species | null) => {
        onChange(next?.id ?? '');
        setQuery('');
      }}
      disabled={disabled}
      nullable
    >
      {required ? (
        <input type="hidden" name={id} value={value} required />
      ) : null}
      <div className={`relative ${wrapperClassName}`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        </div>
        <Combobox.Input
          id={id}
          className="w-full input border-gray-300 shadow-sm sm:text-sm pl-10 pr-10"
          displayValue={(s: Species | null) => (s ? speciesLabel(s) : '')}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setQuery('')}
          placeholder={placeholder}
          autoComplete="off"
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
        </Combobox.Button>

        {filtered.length > 0 ? (
          <Combobox.Options
            className={`absolute z-30 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm ${optionsMaxHeightClass}`}
          >
            {filtered.map((s) => (
              <Combobox.Option
                key={s.id}
                value={s}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                    active ? 'bg-cyan-600 text-white' : 'text-slate-900'
                  }`
                }
              >
                {({ active, selected: isSelected }) => (
                  <>
                    <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                      {speciesLabel(s)}
                    </span>
                    {isSelected ? (
                      <span
                        className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                          active ? 'text-white' : 'text-cyan-600'
                        }`}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        ) : query.trim() ? (
          <div className="absolute z-20 mt-1 w-full rounded-md bg-white px-3 py-2 text-sm text-slate-500 shadow-lg ring-1 ring-black/5">
            No species match “{query.trim()}”
          </div>
        ) : null}
      </div>
    </Combobox>
  );
}
